import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useUnitPreference } from '../../contexts/UnitPreferenceContext'
import { getUserProfile, type User } from '../../services/api'
import { OCAColors, Typography, Spacing } from '../../theme/colors'
import { IOSListSection } from '../ui/IOSListSection'
import { IOSListItem } from '../ui/IOSListItem'
import { IOSSlideUpPanel } from '../ui/IOSSlideUpPanel'
import { MaterialIcon } from '../ui/MaterialIcon'
import omniiqLogo from '../../assets/omniiq_logo.png'

// Custom list item for profile panels with two-line format for long text
const ProfileListItem: React.FC<{
  icon?: React.ReactNode
  title: string
  value: string
  showChevron?: boolean
}> = ({ icon, title, value, showChevron = false }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        padding: `${Spacing.md} ${Spacing.lg}`,
        backgroundColor: OCAColors.background, // White background for list items
        borderBottom: `0.5px solid ${OCAColors.separator}`,
        minHeight: '60px',
      }}
    >
      {/* Icon */}
      {icon && (
        <div style={{
          marginRight: Spacing.md,
          flexShrink: 0,
          marginTop: '2px', // Slight offset to align with title
        }}>
          {icon}
        </div>
      )}

      {/* Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}>
        <div style={{
          fontSize: Typography.fontSize.base,
          fontWeight: Typography.fontWeight.extraLight,
          color: OCAColors.label,
          lineHeight: '20px',
          marginBottom: '4px',
        }}>
          {title}
        </div>
        <div style={{
          fontSize: Typography.fontSize.base,
          fontWeight: Typography.fontWeight.normal,
          color: OCAColors.secondaryLabel,
          lineHeight: '20px',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}>
          {value}
        </div>
      </div>

      {/* Chevron */}
      {showChevron && (
        <div style={{
          marginLeft: Spacing.xs,
          color: OCAColors.tertiaryLabel,
          fontSize: '16px',
          fontWeight: Typography.fontWeight.medium,
          marginTop: '2px',
        }}>
          ›
        </div>
      )}
    </div>
  )
}

export const ProfilePage: React.FC = () => {
  const { user: authUser, getAccessToken, isAuthenticated, logout } = useAuth()
  const { pressureUnit, setPressureUnit } = useUnitPreference()
  const [profile, setProfile] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [activePanel, setActivePanel] = useState<'personal' | 'notifications' | 'password' | 'units' | 'about' | null>(null)
  const [isEditingPersonal, setIsEditingPersonal] = useState(false)
  const [editedProfile, setEditedProfile] = useState<Partial<User>>({})
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      loadProfile()
    }
  }, [isAuthenticated])

  const loadProfile = async () => {
    if (!isAuthenticated) return
    
    try {
      setIsLoading(true)
      setError('')
      
      const token = await getAccessToken()
      if (!token) {
        setError('Unable to get access token')
        return
      }
      
      const profileData = await getUserProfile(token)
      if (profileData) {
        setProfile(profileData)
      } else {
        // Fallback to auth user data if API doesn't return profile
        const fallbackProfile: User = {
          userId: authUser?.id || '',
          email: authUser?.email || '',
          firstName: authUser?.name?.split(' ')[0] || '',
          lastName: authUser?.name?.split(' ').slice(1).join(' ') || '',
          organizationName: authUser?.namespace?.title || '',
          organizationId: authUser?.namespace?.id || '',
        }
        setProfile(fallbackProfile)
      }
    } catch (err) {
      // Use fallback profile even on error
      const fallbackProfile: User = {
        userId: authUser?.id || '',
        email: authUser?.email || '',
        firstName: authUser?.name?.split(' ')[0] || '',
        lastName: authUser?.name?.split(' ').slice(1).join(' ') || '',
        organizationName: authUser?.namespace?.title || '',
        organizationId: authUser?.namespace?.id || '',
      }
      setProfile(fallbackProfile)
      console.error('Error loading profile:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout()
    }
  }

  const handlePersonalInfo = () => {
    setActivePanel('personal')
    setIsEditingPersonal(false)
    setEditedProfile({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      email: profile?.email || '',
    })
  }

  const handleNotificationSettings = () => {
    setActivePanel('notifications')
  }

  const handleChangePassword = () => {
    setActivePanel('password')
  }

  const handleAppInfo = () => {
    setActivePanel('about')
  }

  const handleUnitSettings = () => {
    setActivePanel('units')
  }

  const closePanel = () => {
    setActivePanel(null)
    setIsEditingPersonal(false)
  }

  const handleStartEdit = () => {
    setIsEditingPersonal(true)
  }

  const handleCancelEdit = () => {
    setIsEditingPersonal(false)
    setEditedProfile({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      email: profile?.email || '',
    })
  }

  const handleSavePersonal = async () => {
    setIsSaving(true)
    try {
      // Here you would normally call an API to update the profile
      // For now, just update the local state
      setProfile(prev => prev ? {
        ...prev,
        ...editedProfile
      } : null)
      setIsEditingPersonal(false)
      alert('Profile updated successfully!')
    } catch (err) {
      alert('Failed to update profile. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: OCAColors.textSecondary,
        fontSize: Typography.fontSize.base,
      }}>
        Loading profile...
      </div>
    )
  }

  if (!profile) {
    return (
      <div style={{
        textAlign: 'center',
        padding: Spacing.xl,
        color: OCAColors.textSecondary,
        fontSize: Typography.fontSize.base,
      }}>
        Unable to load profile
      </div>
    )
  }

  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'User'

  return (
    <div style={{
      backgroundColor: '#F2F2F7',
      minHeight: '100vh',
      paddingBottom: '100px',
    }}>
      {/* iOS-style header with profile info */}
      <div style={{
        padding: Spacing.lg,
        backgroundColor: OCAColors.gray900,
        borderBottom: `0.5px solid ${OCAColors.separator}`,
        textAlign: 'center',
        minHeight: '96px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}>
        <img
          src="/OmniIQ_AppIcon_1024x1024.png"
          alt="OMNI IQ"
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '20px',
            display: 'block',
            margin: `0 auto ${Spacing.md} auto`,
          }}
        />
        
        <h1 style={{
          fontSize: Typography.fontSize.xl,
          fontWeight: Typography.fontWeight.bold,
          color: OCAColors.background,
          margin: `0 0 ${Spacing.xs} 0`,
        }}>
          {fullName}
        </h1>

        <div style={{
          fontSize: Typography.fontSize.base,
          color: 'rgba(255, 255, 255, 0.7)',
        }}>
          {profile.email}
        </div>
      </div>

      {error && (
        <div style={{
          padding: Spacing.md,
          margin: Spacing.md,
          backgroundColor: OCAColors.danger + '15',
          borderRadius: '8px',
          color: OCAColors.danger,
          fontSize: Typography.fontSize.sm,
        }}>
          {error}
        </div>
      )}

      {/* Account Information Section */}
      <IOSListSection title="ACCOUNT">
        <IOSListItem
          icon={<div style={{
            width: '40px',
            height: '40px',
            borderRadius: '20px',
            backgroundColor: OCAColors.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MaterialIcon name="person" size={20} color={OCAColors.background} />
          </div>}
          title="Personal Information"
          subtitle="Tap to view details"
          onClick={handlePersonalInfo}
        />
        {profile.organizationName && (
          <IOSListItem
            icon={<div style={{
              width: '40px',
              height: '40px',
              borderRadius: '20px',
              backgroundColor: OCAColors.gray500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <MaterialIcon name="business" size={20} color={OCAColors.background} />
            </div>}
            title="Organization"
            value={profile.organizationName}
            showChevron={false}
          />
        )}
      </IOSListSection>

      {/* Settings Section */}
      <IOSListSection title="SETTINGS">
        <IOSListItem
          icon={<div style={{
            width: '40px',
            height: '40px',
            borderRadius: '20px',
            backgroundColor: OCAColors.success,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MaterialIcon name="speed" size={20} color={OCAColors.background} />
          </div>}
          title="Display Units"
          value={pressureUnit === 'inWC' ? 'Inches Water Column (inWC)' : 'Pascal (Pa)'}
          onClick={handleUnitSettings}
        />
        <IOSListItem
          icon={<div style={{
            width: '40px',
            height: '40px',
            borderRadius: '20px',
            backgroundColor: OCAColors.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MaterialIcon name="notifications" size={20} color={OCAColors.background} />
          </div>}
          title="Notification Settings"
          subtitle="Manage via web dashboard"
          onClick={handleNotificationSettings}
        />
        <IOSListItem
          icon={<div style={{
            width: '40px',
            height: '40px',
            borderRadius: '20px',
            backgroundColor: OCAColors.warning,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MaterialIcon name="lock" size={20} color={OCAColors.background} />
          </div>}
          title="Change Password"
          subtitle="Manage via web dashboard"
          onClick={handleChangePassword}
        />
      </IOSListSection>

      {/* App Information Section */}
      <IOSListSection title="ABOUT">
        <IOSListItem
          icon={<div style={{
            width: '40px',
            height: '40px',
            borderRadius: '20px',
            backgroundColor: OCAColors.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MaterialIcon name="info" size={20} color={OCAColors.background} />
          </div>}
          title="OMNI IQ Mobile"
          subtitle="Tap for version info"
          onClick={handleAppInfo}
        />
      </IOSListSection>

      {/* Logout Section */}
      <IOSListSection>
        <IOSListItem
          icon={<div style={{
            width: '40px',
            height: '40px',
            borderRadius: '20px',
            backgroundColor: OCAColors.danger,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MaterialIcon name="exit_to_app" size={20} color={OCAColors.background} />
          </div>}
          title="Sign Out"
          onClick={handleLogout}
          showChevron={false}
        />
      </IOSListSection>

      {/* Personal Information Panel */}
      <IOSSlideUpPanel
        isOpen={activePanel === 'personal'}
        onClose={closePanel}
        title="Personal Information"
        height="70vh"
      >
        <div style={{ padding: `0 0 ${Spacing.lg} 0` }}>
          {/* Edit/Save/Cancel buttons */}
          <div style={{
            padding: `${Spacing.md} ${Spacing.lg}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: Spacing.sm,
          }}>
            {!isEditingPersonal ? (
              <button
                onClick={handleStartEdit}
                style={{
                  padding: `${Spacing.xs} ${Spacing.md}`,
                  backgroundColor: OCAColors.primary,
                  color: OCAColors.background,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: Typography.fontSize.sm,
                  fontWeight: Typography.fontWeight.medium,
                  cursor: 'pointer',
                }}
              >
                Edit
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancelEdit}
                  style={{
                    padding: `${Spacing.xs} ${Spacing.md}`,
                    backgroundColor: OCAColors.gray200,
                    color: OCAColors.label,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: Typography.fontSize.sm,
                    fontWeight: Typography.fontWeight.medium,
                    cursor: 'pointer',
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePersonal}
                  style={{
                    padding: `${Spacing.xs} ${Spacing.md}`,
                    backgroundColor: OCAColors.success,
                    color: OCAColors.background,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: Typography.fontSize.sm,
                    fontWeight: Typography.fontWeight.medium,
                    cursor: 'pointer',
                    opacity: isSaving ? 0.6 : 1,
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>

          <IOSListSection title="ACCOUNT DETAILS">
            {!isEditingPersonal ? (
              <>
                <ProfileListItem
                  icon={<div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    backgroundColor: OCAColors.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <MaterialIcon name="person" size={20} color={OCAColors.background} />
                  </div>}
                  title="Full Name"
                  value={fullName}
                />
                <ProfileListItem
                  icon={<div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '20px',
                    backgroundColor: OCAColors.gray500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <MaterialIcon name="email" size={20} color={OCAColors.background} />
                  </div>}
                  title="Email Address"
                  value={profile?.email || 'Not specified'}
                />
              </>
            ) : (
              <div style={{ backgroundColor: OCAColors.background }}>
                {/* Editable First Name */}
                <div style={{
                  padding: `${Spacing.md} ${Spacing.lg}`,
                  borderBottom: `0.5px solid ${OCAColors.separator}`,
                }}>
                  <label style={{
                    fontSize: Typography.fontSize.sm,
                    color: OCAColors.secondaryLabel,
                    marginBottom: Spacing.xs,
                    display: 'block',
                  }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={editedProfile.firstName || ''}
                    onChange={(e) => setEditedProfile(prev => ({ ...prev, firstName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: `${Spacing.sm} 0`,
                      fontSize: Typography.fontSize.base,
                      color: OCAColors.label,
                      backgroundColor: 'transparent',
                      border: 'none',
                      outline: 'none',
                    }}
                    placeholder="Enter first name"
                  />
                </div>

                {/* Editable Last Name */}
                <div style={{
                  padding: `${Spacing.md} ${Spacing.lg}`,
                  borderBottom: `0.5px solid ${OCAColors.separator}`,
                }}>
                  <label style={{
                    fontSize: Typography.fontSize.sm,
                    color: OCAColors.secondaryLabel,
                    marginBottom: Spacing.xs,
                    display: 'block',
                  }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={editedProfile.lastName || ''}
                    onChange={(e) => setEditedProfile(prev => ({ ...prev, lastName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: `${Spacing.sm} 0`,
                      fontSize: Typography.fontSize.base,
                      color: OCAColors.label,
                      backgroundColor: 'transparent',
                      border: 'none',
                      outline: 'none',
                    }}
                    placeholder="Enter last name"
                  />
                </div>

                {/* Editable Email */}
                <div style={{
                  padding: `${Spacing.md} ${Spacing.lg}`,
                  borderBottom: `0.5px solid ${OCAColors.separator}`,
                }}>
                  <label style={{
                    fontSize: Typography.fontSize.sm,
                    color: OCAColors.secondaryLabel,
                    marginBottom: Spacing.xs,
                    display: 'block',
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={editedProfile.email || ''}
                    onChange={(e) => setEditedProfile(prev => ({ ...prev, email: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: `${Spacing.sm} 0`,
                      fontSize: Typography.fontSize.base,
                      color: OCAColors.label,
                      backgroundColor: 'transparent',
                      border: 'none',
                      outline: 'none',
                    }}
                    placeholder="Enter email address"
                  />
                </div>
              </div>
            )}

            {profile?.organizationName && (
              <ProfileListItem
                icon={<div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '20px',
                  backgroundColor: OCAColors.warning,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <MaterialIcon name="business" size={20} color={OCAColors.background} />
                </div>}
                title="Organization"
                value={profile.organizationName}
              />
            )}
          </IOSListSection>
        </div>
      </IOSSlideUpPanel>

      {/* Notification Settings Panel */}
      <IOSSlideUpPanel
        isOpen={activePanel === 'notifications'}
        onClose={closePanel}
        title="Notification Settings"
        height="50vh"
      >
        <div style={{ padding: `${Spacing.lg}` }}>
          <div style={{
            textAlign: 'center',
            padding: `${Spacing.xl} ${Spacing.md}`,
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: Spacing.md,
            }}>
              <MaterialIcon name="web" size={48} color={OCAColors.primary} />
            </div>
            <h3 style={{
              fontSize: Typography.fontSize.lg,
              fontWeight: Typography.fontWeight.bold,
              color: OCAColors.label,
              margin: `0 0 ${Spacing.sm} 0`,
            }}>
              Web Dashboard Required
            </h3>
            <p style={{
              fontSize: Typography.fontSize.base,
              color: OCAColors.secondaryLabel,
              lineHeight: '1.5',
              margin: 0,
            }}>
              Notification settings are managed through your web dashboard. Please visit the main OMNI IQ web application to configure email alerts, SMS notifications, and escalation policies.
            </p>
          </div>
        </div>
      </IOSSlideUpPanel>

      {/* Change Password Panel */}
      <IOSSlideUpPanel
        isOpen={activePanel === 'password'}
        onClose={closePanel}
        title="Change Password"
        height="50vh"
      >
        <div style={{ padding: `${Spacing.lg}` }}>
          <div style={{
            textAlign: 'center',
            padding: `${Spacing.xl} ${Spacing.md}`,
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: Spacing.md,
            }}>
              <MaterialIcon name="security" size={48} color={OCAColors.warning} />
            </div>
            <h3 style={{
              fontSize: Typography.fontSize.lg,
              fontWeight: Typography.fontWeight.bold,
              color: OCAColors.label,
              margin: `0 0 ${Spacing.sm} 0`,
            }}>
              Web Dashboard Required
            </h3>
            <p style={{
              fontSize: Typography.fontSize.base,
              color: OCAColors.secondaryLabel,
              lineHeight: '1.5',
              margin: 0,
            }}>
              Password changes are managed through the main OMNI IQ web application. Please visit the web dashboard to update your password and security settings.
            </p>
          </div>
        </div>
      </IOSSlideUpPanel>

      {/* Display Units Panel */}
      <IOSSlideUpPanel
        isOpen={activePanel === 'units'}
        onClose={closePanel}
        title="Display Units"
        height="50vh"
      >
        <div style={{ padding: `0 0 ${Spacing.lg} 0` }}>
          <IOSListSection title="PRESSURE UNITS">
            <div
              onClick={() => setPressureUnit('inWC')}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                borderBottom: `0.5px solid ${OCAColors.separator}`,
                cursor: 'pointer',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: Typography.fontSize.base,
                  fontWeight: Typography.fontWeight.medium,
                  color: OCAColors.label,
                  marginBottom: '4px',
                }}>
                  Inches Water Column
                </div>
                <div style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.secondaryLabel,
                }}>
                  inWC (4 decimal places)
                </div>
              </div>
              {pressureUnit === 'inWC' && (
                <div style={{
                  color: OCAColors.primary,
                  fontSize: '20px',
                  fontWeight: Typography.fontWeight.bold,
                }}>
                  ✓
                </div>
              )}
            </div>

            <div
              onClick={() => setPressureUnit('Pa')}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: `${Spacing.md} ${Spacing.lg}`,
                backgroundColor: OCAColors.background,
                cursor: 'pointer',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: Typography.fontSize.base,
                  fontWeight: Typography.fontWeight.medium,
                  color: OCAColors.label,
                  marginBottom: '4px',
                }}>
                  Pascal
                </div>
                <div style={{
                  fontSize: Typography.fontSize.sm,
                  color: OCAColors.secondaryLabel,
                }}>
                  Pa (2 decimal places)
                </div>
              </div>
              {pressureUnit === 'Pa' && (
                <div style={{
                  color: OCAColors.primary,
                  fontSize: '20px',
                  fontWeight: Typography.fontWeight.bold,
                }}>
                  ✓
                </div>
              )}
            </div>
          </IOSListSection>

          <div style={{
            padding: `${Spacing.lg}`,
            fontSize: Typography.fontSize.sm,
            color: OCAColors.secondaryLabel,
            lineHeight: '1.5',
          }}>
            This setting controls how pressure values are displayed throughout the app.
            All device data is stored in inWC and converted for display only.
          </div>
        </div>
      </IOSSlideUpPanel>

      {/* About App Panel */}
      <IOSSlideUpPanel
        isOpen={activePanel === 'about'}
        onClose={closePanel}
        title="About OMNI IQ Mobile"
        height="70vh"
      >
        <div style={{ padding: `0 0 ${Spacing.lg} 0` }}>
          {/* Logo Header */}
          <div style={{
            textAlign: 'center',
            padding: `${Spacing.xl} ${Spacing.lg}`,
            borderBottom: `0.5px solid ${OCAColors.separator}`,
          }}>
            <img
              src={omniiqLogo}
              alt="OMNI IQ Logo"
              onLoad={() => console.log('Logo loaded successfully in header:', omniiqLogo)}
              onError={(e) => {
                console.error('Logo failed to load in header:', omniiqLogo);
                console.error('Image error:', e);
              }}
              style={{
                width: '200px',
                height: 'auto',
                maxHeight: '60px',
                objectFit: 'contain',
                marginBottom: Spacing.md
              }}
            />
          </div>

          <IOSListSection title="APP INFORMATION">
            <IOSListItem
              icon={<div style={{
                width: '40px',
                height: '40px',
                borderRadius: '20px',
                backgroundColor: OCAColors.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <MaterialIcon name="info" size={20} color={OCAColors.background} />
              </div>}
              title="Version"
              value="1.0.0"
              showChevron={false}
            />
            <IOSListItem
              icon={<div style={{
                width: '40px',
                height: '40px',
                borderRadius: '20px',
                backgroundColor: OCAColors.success,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <MaterialIcon name="build" size={20} color={OCAColors.background} />
              </div>}
              title="Build"
              value="Production"
              showChevron={false}
            />
            <IOSListItem
              icon={<div style={{
                width: '40px',
                height: '40px',
                borderRadius: '20px',
                backgroundColor: OCAColors.warning,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <MaterialIcon name="copyright" size={20} color={OCAColors.background} />
              </div>}
              title="Copyright"
              value="© Omni CleanAir 2025"
              showChevron={false}
            />
          </IOSListSection>
        </div>
      </IOSSlideUpPanel>
    </div>
  )
}